import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {getSafely, Nullish} from 'shared/web';

import {isTransmissionExcluded} from '../../../../../constants/transmissionOptionsExcluded';
import {MIN_FIRST_REGISTRATION_YEAR} from '../../../../../pages/Create/constants/minFirstRegistrationYear';
import {
  GetTrimLevelsApiResponse,
  NumericBoundBody,
  RelativeFilterChoice,
  RelativeFilterConfigBody,
} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {convertToPercentage} from '../../../../../utils/convertToPercentage';
import {getSimilarBodyStyles} from '../../../../../utils/getSimilarBodyStyles';
import {getBetterOrEqualValues} from './getBetterOrEqualValues';
import {getSameAndBetterTrimLevelConstKeys} from './getSameAndBetterTrimLevelConstKeys';

/** 100% cap: feature score filter range, feature score preset offsets, and max vehicle − for mileage/power/price %. */
const MAX_PERCENT = 100;

export type PresetApplyContext = {
  config: RelativeFilterConfigBody;
  vehicle: PriceReportType['vehicle'];
  recommendedPrice: number;
  rawFeatureLevel: number | Nullish;
  trimLevelsCatalog: GetTrimLevelsApiResponse['trim_levels'] | null;
};

function currentCalendarYear(): number {
  return new Date().getFullYear();
}

function sanitizeVehicleMetricPctBound(bound: NumericBoundBody): NumericBoundBody {
  return {
    from: isNil(bound.from) ? null : Math.max(-MAX_PERCENT, Math.min(0, bound.from)),
    to: isNil(bound.to) ? null : Math.max(0, bound.to),
  };
}

function clampNonNegative(value: number | null): number | null {
  if (isNil(value)) {
    return null;
  }
  return Math.max(0, value);
}

function clampFeatureScore(value: number | null): number | null {
  if (isNil(value)) {
    return null;
  }
  return Math.min(MAX_PERCENT, Math.max(0, value));
}

function clampRegistrationYear(value: number | null): number | null {
  if (isNil(value)) {
    return null;
  }
  return Math.min(currentCalendarYear(), Math.max(MIN_FIRST_REGISTRATION_YEAR, value));
}

function normalizeRegistrationYearBounds(
  gte: number | null,
  lte: number | null
): {gte: number | null; lte: number | null} {
  const g = clampRegistrationYear(gte);
  let l = clampRegistrationYear(lte);
  if (isNotNil(g) && isNotNil(l) && g > l) {
    l = g;
  }
  return {gte: g, lte: l};
}

function resolveEnd(signedOffset: number | null, base: number, unit: 'abs' | 'pct'): number | null {
  if (isNil(signedOffset)) {
    return null;
  }
  if (unit === 'pct') {
    return Math.round(base * (1 + signedOffset / 100));
  }
  return Math.round(base + signedOffset);
}

function resolveBoundPair(
  abs: NumericBoundBody | null,
  pct: NumericBoundBody | null,
  base: number,
  priceBase?: number
): {gte: number | null; lte: number | null} {
  const activeRaw = abs ?? pct;
  if (isNil(activeRaw)) {
    return {gte: null, lte: null};
  }
  const unit: 'abs' | 'pct' = isNotNil(abs) ? 'abs' : 'pct';
  const active = unit === 'pct' ? sanitizeVehicleMetricPctBound(activeRaw) : activeRaw;
  const effectiveBase = unit === 'pct' ? (priceBase ?? base) : base;
  return {
    gte: resolveEnd(active.from, effectiveBase, unit),
    lte: resolveEnd(active.to, effectiveBase, unit),
  };
}

function resolveSingleBound(
  bound: NumericBoundBody | null,
  base: number | Nullish
): {gte: number | null; lte: number | null} {
  if (isNil(bound)) {
    return {gte: null, lte: null};
  }
  const b = base ?? 0;
  return {
    gte: resolveEnd(bound.from, b, 'abs'),
    lte: resolveEnd(bound.to, b, 'abs'),
  };
}

function toFeatureScoreFilterBase(featuresLevel: number): number {
  return featuresLevel > 1 ? Math.round(featuresLevel) : convertToPercentage(featuresLevel);
}

/**
 * Feature score is already on a 0–100 % scale; preset offsets are point deltas on that scale
 * (e.g. −1 / +1 → vehicle score minus one point, plus one point), not % of the score.
 */
function resolveFeatureScorePresetBound(
  bound: NumericBoundBody | null,
  rawFeaturesLevel: number | Nullish
): {gte: number | null; lte: number | null} {
  if (isNil(bound)) {
    return {gte: null, lte: null};
  }
  if (isNil(rawFeaturesLevel)) {
    return {gte: null, lte: null};
  }
  const basePoints = toFeatureScoreFilterBase(rawFeaturesLevel);
  return {
    gte: resolveEnd(bound.from, basePoints, 'abs'),
    lte: resolveEnd(bound.to, basePoints, 'abs'),
  };
}

function resolveRegistrationYearBound(
  bound: NumericBoundBody | null,
  base: number | Nullish
): {gte: number | null; lte: number | null} {
  if (isNil(bound) || isNil(base)) {
    return {gte: null, lte: null};
  }
  const raw = resolveSingleBound(bound, base);
  return normalizeRegistrationYearBounds(raw.gte, raw.lte);
}

type ThreeWayMode = 'any' | 'same' | 'same_better';

function resolveThreeWay(mode: RelativeFilterChoice | null): ThreeWayMode {
  return match<RelativeFilterChoice | null, ThreeWayMode>(mode)
    .with(Pattern.nullish, () => 'any')
    .otherwise((resolved) => resolved);
}

function resolveTransmission(ctx: PresetApplyContext): string[] {
  const {config, vehicle} = ctx;
  return match<ThreeWayMode, string[]>(resolveThreeWay(config.transmission))
    .with('any', () => [])
    .with('same_better', () =>
      getBetterOrEqualValues('transmission', vehicle.transmission).filter(
        (t) => !isTransmissionExcluded(t)
      )
    )
    .with('same', () =>
      match<boolean, string[]>(isTransmissionExcluded(vehicle.transmission))
        .with(true, () => [])
        .with(false, () => [vehicle.transmission])
        .exhaustive()
    )
    .exhaustive();
}

function resolveDrive(ctx: PresetApplyContext): string[] {
  const {config, vehicle} = ctx;
  return match<ThreeWayMode, string[]>(resolveThreeWay(config.drive))
    .with('any', () => [])
    .with('same_better', () => getBetterOrEqualValues('drive', vehicle.drive))
    .with('same', () => [vehicle.drive])
    .exhaustive();
}

function resolveVatDeductible(ctx: PresetApplyContext): ('YES' | 'NO')[] {
  const {config, vehicle} = ctx;
  const vehicleVat: 'YES' | 'NO' = vehicle.isVatDeductible === true ? 'YES' : 'NO';
  return match<ThreeWayMode, ('YES' | 'NO')[]>(resolveThreeWay(config.vatDeductible))
    .with('any', () => [])
    .with('same_better', () => {
      const isVatValue = (v: string): v is 'YES' | 'NO' => v === 'YES' || v === 'NO';
      return getBetterOrEqualValues('vat', vehicleVat).filter(isVatValue);
    })
    .with('same', () => [vehicleVat])
    .exhaustive();
}

function resolveTrimLevels(ctx: PresetApplyContext): string[] {
  const {config, vehicle, trimLevelsCatalog} = ctx;
  return match<ThreeWayMode, string[]>(resolveThreeWay(config.trimLevel))
    .with('any', () => [])
    .with('same_better', () =>
      getSameAndBetterTrimLevelConstKeys(trimLevelsCatalog, vehicle.trimLevel)
    )
    .with('same', () =>
      match<typeof vehicle.trimLevel, string[]>(vehicle.trimLevel)
        .with(Pattern.nullish, () => [])
        .otherwise((trimLevel) => [trimLevel])
    )
    .exhaustive();
}

/**
 * Single source of truth for filter form keys owned by saved presets.
 * Add or remove a preset field here only — {@link PRESET_CONTROLLED_FORM_FIELDS} is derived from these keys.
 */
export const PRESET_FIELD_RESOLVERS = {
  firstRegistrationOnYearGte(ctx: PresetApplyContext) {
    return resolveRegistrationYearBound(ctx.config.year, ctx.vehicle.firstRegistrationOnYear).gte;
  },
  firstRegistrationOnYearLte(ctx: PresetApplyContext) {
    return resolveRegistrationYearBound(ctx.config.year, ctx.vehicle.firstRegistrationOnYear).lte;
  },
  mileageGte(ctx: PresetApplyContext) {
    const {gte} = resolveBoundPair(
      ctx.config.mileage,
      ctx.config.mileagePct,
      ctx.vehicle.mileage ?? 0
    );
    return clampNonNegative(gte);
  },
  mileageLte(ctx: PresetApplyContext) {
    const {lte} = resolveBoundPair(
      ctx.config.mileage,
      ctx.config.mileagePct,
      ctx.vehicle.mileage ?? 0
    );
    return clampNonNegative(lte);
  },
  powerGte(ctx: PresetApplyContext) {
    const vehiclePower = ctx.vehicle.maxPower ?? ctx.vehicle.power ?? 0;
    const {gte} = resolveBoundPair(ctx.config.power, ctx.config.powerPct, vehiclePower);
    return clampNonNegative(gte);
  },
  powerLte(ctx: PresetApplyContext) {
    const vehiclePower = ctx.vehicle.maxPower ?? ctx.vehicle.power ?? 0;
    const {lte} = resolveBoundPair(ctx.config.power, ctx.config.powerPct, vehiclePower);
    return clampNonNegative(lte);
  },
  priceGte(ctx: PresetApplyContext) {
    const {gte} = resolveBoundPair(
      ctx.config.price,
      ctx.config.pricePct,
      ctx.recommendedPrice,
      ctx.recommendedPrice
    );
    return clampNonNegative(gte);
  },
  priceLte(ctx: PresetApplyContext) {
    const {lte} = resolveBoundPair(
      ctx.config.price,
      ctx.config.pricePct,
      ctx.recommendedPrice,
      ctx.recommendedPrice
    );
    return clampNonNegative(lte);
  },
  featureScoreGte(ctx: PresetApplyContext) {
    const {gte} = resolveFeatureScorePresetBound(ctx.config.featureScore, ctx.rawFeatureLevel);
    return clampFeatureScore(gte);
  },
  featureScoreLte(ctx: PresetApplyContext) {
    const {lte} = resolveFeatureScorePresetBound(ctx.config.featureScore, ctx.rawFeatureLevel);
    return clampFeatureScore(lte);
  },
  radius(ctx: PresetApplyContext) {
    return ctx.config.radius;
  },
  transmission: resolveTransmission,
  fuelType(ctx: PresetApplyContext) {
    return ctx.config.fuel === 'same' ? [ctx.vehicle.fuelType] : [];
  },
  bodyStyle(ctx: PresetApplyContext) {
    return ctx.config.body === 'same' ? getSimilarBodyStyles(ctx.vehicle.bodyStyle) : [];
  },
  drive: resolveDrive,
  isVatDeductible: resolveVatDeductible,
  trimLevels: resolveTrimLevels,
  // sellerType is omitted — filter exists but UI/preset editor are suppressed; do not
  // reset it when applying a preset or treat edits as preset drift (see getPresetTags).
};

export type PresetControlledFormField = keyof typeof PRESET_FIELD_RESOLVERS;

type PresetFieldResolverMap = {
  [K in PresetControlledFormField]: (ctx: PresetApplyContext) => FilterFormData[K];
};

const presetFieldResolvers: PresetFieldResolverMap = PRESET_FIELD_RESOLVERS;

function typedKeysOf<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

export const PRESET_CONTROLLED_FORM_FIELDS: PresetControlledFormField[] =
  typedKeysOf(PRESET_FIELD_RESOLVERS);

export function buildPresetApplyContext(
  config: RelativeFilterConfigBody,
  priceReport: PriceReportType,
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null
): PresetApplyContext {
  return {
    config,
    vehicle: priceReport.vehicle,
    recommendedPrice: priceReport.sellingPrice.recommended.value ?? 0,
    rawFeatureLevel:
      priceReport.featuresLevel ?? priceReport.additionalSettings?.featuresLevel ?? null,
    trimLevelsCatalog: trimLevelsCatalog ?? null,
  };
}

export function resolvePresetFormFieldValue<K extends PresetControlledFormField>(
  field: K,
  ctx: PresetApplyContext
): FilterFormData[K] {
  const resolver = getSafely(presetFieldResolvers, field);
  if (isNil(resolver)) {
    throw new Error(`Missing preset field resolver for "${String(field)}"`);
  }
  return resolver(ctx);
}
